import { Helmet } from "react-helmet-async";
import ProductSchema from "../components/ProductSchema";
import { Link, useNavigate } from "react-router-dom";
import CosmicBackground from "../components/CosmicBackground";
import styles from "./AiAstrologerPage.module.css";

// ADDITIVE JYOTISH SIGNALS 2026-10-13 (item 18): GSC 28d shows the jyotish
// cluster sitting on the HOMEPAGE ("ai jyotish" 98 clicks / 3,021 imp @6.8 with
// 3.2% CTR, "ai jyotish free" 72 / 1,791 @7.2) while this page had 1 click /
// 33 imp @10.9 for the exact term. Jyotish wording added to the eyebrow, sub,
// meta description and one section WITHOUT touching the item-13 title win
// ("AI Astrologer Free Chat: Online Vedic AI, 24x7"). Full ownership of the
// cluster moves to the dedicated Hinglish jyotish page planned as item 23.

// FAQ content lives here once, and drives BOTH the visible list and the FAQPage
// JSON-LD, so the structured data always matches what a user sees on the page.
const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: "Is an AI astrologer accurate?",
    a: "Aura AI calculates your planetary positions with the same Swiss Ephemeris astronomy that professional astrologers use, then interprets your chart using classical Vedic (Jyotish) principles. The maths is exact; the guidance is only as good as the tradition it is trained on, which is why we ground every answer in established Vedic texts rather than guesses.",
  },
  {
    q: "Is the AI astrologer free to use?",
    a: "Yes. You can generate your free kundli, view your birth chart and ask your first question to the AI astrologer for free, with no card required. After that, chats are paid from wallet credits, so you only pay for what you actually use. There is no subscription and nothing auto-renews.",
  },
  {
    q: "Does it use Vedic astrology or Western astrology?",
    a: "Aura AI is built on Vedic astrology (Jyotish): your Moon sign, nakshatra, dashas and divisional charts. It is designed for Indian users but works for anyone, anywhere in the world.",
  },
  {
    q: "Can the AI astrologer read my kundli and birth chart?",
    a: "Yes. Enter your date, time and place of birth and Aura AI instantly builds your full kundli, then reads your houses, planets, mahadasha timeline and yogas to answer questions about your life.",
  },
  {
    q: "Is my birth data private?",
    a: "Your birth details are used only to generate your chart and are never sold. You are in control of your data at all times.",
  },
  {
    q: "Does the AI astrologer work in Hindi?",
    a: "Yes. You can chat with Aura AI in English or Hindi and get answers in the language you are most comfortable with.",
  },
  {
    q: "Can an AI astrologer predict my future?",
    a: "No astrologer, human or AI, can see events the way a camera sees a street. What a chart supports is pattern and timing: which areas of life are activated in the dasha you are running, and which windows look easier or harder for a given kind of decision. Aura AI gives you that, with the reasoning shown, and leaves the deciding to you.",
  },
  {
    q: "Does the AI astrologer use my real birth chart?",
    a: "Yes. Every answer starts from your own kundli, computed with Swiss Ephemeris astronomy data from the date, time and place of birth you enter. It is not a canned reading for your sun sign: change the birth time and the answer changes with the chart.",
  },
];

export default function AiAstrologerPage() {
  const navigate = useNavigate();
  const goToChat = () => navigate("/chat");

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "AI Astrologer - Aura AI",
    serviceType: "AI Vedic astrology consultation",
    url: "https://astroaura.ai/ai-astrologer",
    areaServed: "IN",
    provider: {
      "@type": "Organization",
      name: "Aura AI",
      url: "https://astroaura.ai/",
    },
    description:
      "Ask an AI Vedic astrologer online. Get your free kundli and birth chart and instant, personalised answers on career, love, marriage, money, health and timing.",
    offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
  };

  /* FAQPage JSON-LD removed 2026-10-23: FAQ rich results retired 7 May
     2026. The visible FAQ stays. */

  return (
    <div className={styles.page}>
      <ProductSchema />
      <Helmet>
        <title>AI Astrologer: Ask an Online Vedic AI, 24x7 | Aura AI</title>
        <meta
          name="description"
          content="Talk to an AI astrologer free. Aura AI is an AI jyotish giving instant answers on career, love and money, with a free kundli and Vedic birth chart. Chat 24x7."
        />
        <link rel="canonical" href="https://astroaura.ai/ai-astrologer" />
        <meta
          property="og:title"
          content="AI Astrologer: Ask an Online Vedic AI, 24x7 | Aura AI"
        />
        <meta
          property="og:description"
          content="Talk to an AI astrologer free. Aura AI is an AI jyotish: instant answers on career, love and money, with a free kundli and Vedic birth chart."
        />
        <meta property="og:url" content="https://astroaura.ai/ai-astrologer" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://astroaura.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="AI Astrologer: Ask an Online Vedic AI, 24x7 | Aura AI"
        />
        <meta
          name="twitter:description"
          content="Talk to an AI astrologer free. Aura AI is an AI jyotish: instant answers on career, love and money, with a free kundli and Vedic birth chart."
        />
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://astroaura.ai/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "AI Astrologer",
                item: "https://astroaura.ai/ai-astrologer",
              },
            ],
          })}
        </script>
      </Helmet>

      <CosmicBackground />

      <main className={styles.wrap}>
        <header className={styles.hero}>
          <p className={styles.eyebrow}>AI Astrology and Jyotish, made personal</p>
          <h1 className={styles.h1}>
            Talk to Your <span className={styles.accent}>AI Astrologer</span>,
            Free
          </h1>
          <p className={styles.sub}>
            Aura AI is an AI Vedic astrologer, an AI jyotish, that reads your
            birth chart and answers your real questions about career, love,
            money, marriage, health and timing, instantly and 24x7. Ask your
            first question free.
          </p>
          <div className={styles.ctaRow}>
            <button className={styles.ctaPrimary} onClick={goToChat}>
              Ask your first question free
            </button>
            <button className={styles.ctaGhost} onClick={goToChat}>
              Get your free kundli
            </button>
          </div>
        </header>

        <section className={styles.section}>
          <h2 className={styles.h2}>What is an AI astrologer?</h2>
          <p>
            An AI astrologer is software that combines exact astronomical
            calculation with artificial intelligence to read your horoscope the
            way a Vedic astrologer would. It takes your date, time and place of
            birth, computes the positions of the planets using the Swiss
            Ephemeris, and then interprets that chart using classical Jyotish
            principles, delivering personalised AI astrology guidance in seconds
            instead of days.
          </p>
          <p>
            Unlike a generic chatbot, a real AI astrologer is grounded in the
            structure of Vedic astrology: your ascendant, the twelve houses,
            planetary lords, nakshatras, yogas and the mahadasha timeline that
            shapes the timing of events in your life.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>How Aura AI&apos;s AI astrologer works</h2>
          <ol className={styles.steps}>
            <li>
              <strong>Share your birth details.</strong> Enter your date, time
              and place of birth. That is all Aura AI needs.
            </li>
            <li>
              <strong>Get your free kundli instantly.</strong> Aura AI builds
              your full Vedic birth chart, or kundli, with your houses, planets
              and dashas.
            </li>
            <li>
              <strong>Ask anything.</strong> Chat naturally about career, money,
              love, marriage, health or the right timing for a decision, and get
              answers rooted in your own chart.
            </li>
            <li>
              <strong>Go deeper.</strong> Explore your mahadasha timeline, daily
              horoscope, kundli matching and remedies matched to your stars.
            </li>
          </ol>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>Why choose an AI astrologer</h2>
          <div className={styles.grid}>
            <div className={styles.card}>
              <h3>Always available</h3>
              <p>
                Your AI astrologer answers 24x7, so you never wait for an
                appointment.
              </p>
            </div>
            <div className={styles.card}>
              <h3>Instant and personal</h3>
              <p>
                Every answer is computed from your own birth chart, not a
                generic sun-sign forecast.
              </p>
            </div>
            <div className={styles.card}>
              <h3>Private</h3>
              <p>
                Ask sensitive questions freely. Your birth data stays yours and
                is never sold.
              </p>
            </div>
            <div className={styles.card}>
              <h3>Free to start</h3>
              <p>
                Generate your free kundli and ask your first question at no
                cost, in English or Hindi.
              </p>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>What you can ask your AI astrologer</h2>
          <ul className={styles.asks}>
            <li>When will my career finally take off?</li>
            <li>Is this the right time to change jobs or start a business?</li>
            <li>What does my kundli say about marriage and love?</li>
            <li>Are my partner and I compatible? (kundli matching)</li>
            <li>Which mahadasha am I running and what does it mean?</li>
            <li>What remedies can strengthen a weak planet in my chart?</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>AI astrologer vs a human astrologer</h2>
          <p>
            A good human astrologer brings intuition and years of experience to
            reading the subtle layers of a chart. An AI astrologer brings speed,
            consistency and availability: the same rigorous calculation every
            time, no waiting, no judgement, and no rising consultation fees. Aura
            AI is built to give you the everyday clarity of a trusted astrologer
            in your pocket, and to point you to a deeper reading whenever you
            want one. For a full side-by-side, read{" "}
            <Link to="/blog/ai-astrology-vs-human-astrology">
              AI astrology vs human astrology
            </Link>
            .
          </p>
          <p>
            Wondering how a purpose-built AI astrologer differs from asking a
            general chatbot? See{" "}
            <Link to="/blog/ai-astrology-vs-chatgpt">
              AI astrology vs ChatGPT
            </Link>
            .
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>What an AI astrologer reads in your chart</h2>
          <p>
            A serious reading never starts from your sun sign. When you ask
            Aura AI a question, it works from the same chart factors a
            classical Jyotish reading would, computed to the exact degree for
            your birth time and place:
          </p>
          <ul className={styles.steps}>
            <li>
              <strong>Lagna and houses.</strong> The ascendant fixes the
              twelve houses, so the same planet means different things for
              different people. Career questions read the tenth house and its
              lord, marriage questions the seventh, money the second and
              eleventh.
            </li>
            <li>
              <strong>The Moon and your nakshatra.</strong> The Moon sign is
              what rashifal is actually based on, and the nakshatra sharpens
              it further, down to the pada. This is also what the Vimshottari
              dasha sequence is counted from.
            </li>
            <li>
              <strong>Mahadasha and antardasha.</strong> Timing comes from
              the dasha running now, not from a transit alone. An AI
              astrologer that ignores your dasha is guessing; Aura AI reads
              it first.
            </li>
            <li>
              <strong>Yogas and doshas.</strong> Raj yogas, dhana yogas,
              manglik and kaal sarp conditions, checked with their
              cancellation rules, because most doshas come with exceptions
              that popular articles skip.
            </li>
            <li>
              <strong>Current transits.</strong> Where Shani, Guru and Rahu
              stand today against your birth positions, which is what turns
              a static chart into this month's answer.
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>
            Career, marriage and money: how the answers differ
          </h2>
          <p>
            The most common questions people bring to an AI astrologer fall
            into three buckets, and each one is read from a different part of
            the chart. A career answer weighs the tenth house, its lord's
            strength, the dasha running now and Shani's transit, which is why
            "should I switch jobs" gets a different answer in a Shani
            antardasha than in a Guru one. A marriage answer starts from the
            seventh house and Shukra, checks manglik status honestly, with
            cancellations, and only then talks about timing windows. A money
            answer separates earning (eleventh house) from saving (second)
            from sudden gains and losses (eighth), because "will I be rich"
            is really three different questions.
          </p>
          <p>
            This is the practical difference between an AI astrologer and a
            daily horoscope: the horoscope gives one paragraph to a twelfth
            of humanity, while the chart-based answer is computed from your
            own birth details and explains which placement it is reading, so
            you can check the reasoning instead of taking it on faith.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>
            When to trust an AI astrologer, and when not to
          </h2>
          <p>
            An honest tool should say what it is not for. Aura AI does not
            replace a doctor, a lawyer or a financial adviser, and it will
            tell you so when a question needs one. It does not quote fake
            accuracy percentages, and it can be wrong, the same way any
            interpretation of a chart can be. What it is good at is
            structure and timing: showing you what your chart actually
            contains, which periods look supportive for a decision you are
            already weighing, and which classical rules apply to your
            situation, with the reasoning stated so you can judge it.
          </p>
          <p>
            Use it the way people have always used Jyotish at its best: as a
            second opinion with a very long memory, not as a verdict. The
            full method, including what we deliberately do not claim, is
            written up on the <Link to="/methodology">methodology page</Link>.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>From first question to remedy: how a session flows</h2>
          <p>
            A session with the AI astrologer is a conversation, not a form.
            You ask in plain language, in English or Hindi, and the first
            question costs nothing, no card required. The answer names the
            chart factors it used, so a typical reply looks like a short
            verdict followed by the reasoning: which house was read, which
            dasha is running, which transit matters this month. You can push
            back, ask why, or narrow the question, the way you would with a
            person, and the thread keeps your chart in context.
          </p>
          <p>
            On remedies, the approach is deliberately conservative. The chat
            will explain which classical remedy tradition attaches to a
            placement and when a gemstone is traditionally contraindicated,
            but it will not frighten you into a purchase: most doshas carry
            cancellation rules, and the honest first remedy is usually
            understanding what the chart actually says. After the free
            start, chats are paid from wallet credits with no per-minute
            meter, and regular consulters can take an optional subscription
            from the pricing page.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>Looking for a free AI jyotish?</h2>
          <p>
            Jyotish is the Sanskrit name for Vedic astrology, so an AI jyotish
            is exactly what Aura AI is: an AI trained to read your kundli the
            classical way, through your lagna, nakshatra, dashas and yogas.
            Chat with the AI jyotish free in Hindi or English, on the web or in
            the app, and get answers in the language you actually think in.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>AI astrology, frequently asked questions</h2>
          <div className={styles.faq}>
            {FAQ_ITEMS.map((item) => (
              <details key={item.q} className={styles.faqItem}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className={styles.finalCta}>
          <h2 className={styles.h2}>Meet your AI astrologer now</h2>
          <p>
            Your first question is free. Get your kundli and start chatting with
            Aura AI, your personal AI Vedic astrologer.
          </p>
          <button className={styles.ctaPrimary} onClick={goToChat}>
            Start free
          </button>
          <p className={styles.links}>
            Prefer to just start talking? Try the{" "}
            <Link to="/talk-to-astrologer">free astrology chat</Link>. Learn{" "}
            <Link to="/how-it-works">how Aura AI works</Link>, see{" "}
            <Link to="/pricing">pricing</Link>, or go back{" "}
            <Link to="/">home</Link>.
          </p>
        </section>
      </main>
    </div>
  );
}
